(function () {
  'use strict';

  const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS_FULL    = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const DAYS_SHORT   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const pad          = (n) => String(n).padStart(2, '0');

  function formatDate(dateStr, fmt) {
    if (!dateStr) return '';
    const [y, m, dd] = dateStr.split('-');
    const d = new Date(Date.UTC(+y, +m - 1, +dd));
    return fmt
      .replace('%B', MONTHS_FULL[d.getUTCMonth()])
      .replace('%b', MONTHS_SHORT[d.getUTCMonth()])
      .replace('%m', pad(d.getUTCMonth() + 1))
      .replace('%d', pad(d.getUTCDate()))
      .replace('%e', String(d.getUTCDate()).padStart(2, ' '))
      .replace('%Y', String(d.getUTCFullYear()))
      .replace('%y', String(d.getUTCFullYear()).slice(-2))
      .replace('%A', DAYS_FULL[d.getUTCDay()])
      .replace('%a', DAYS_SHORT[d.getUTCDay()]);
  }

  function offsetDateFormatted(offsetDays, fmt) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return fmt
      .replace('%B', MONTHS_FULL[d.getMonth()])
      .replace('%b', MONTHS_SHORT[d.getMonth()])
      .replace('%m', pad(d.getMonth() + 1))
      .replace('%d', pad(d.getDate()))
      .replace('%e', String(d.getDate()).padStart(2, ' '))
      .replace('%Y', String(d.getFullYear()))
      .replace('%y', String(d.getFullYear()).slice(-2));
  }

  function escapeHTML(str) {
    return String(str).replace(/[&<>"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
    );
  }

  function buildInventoryBadge(vData) {
    if (!vData) return '';
    if (vData.inventory_management === 'shopify') {
      if (vData.qty > 0)
        return '<span class="stock-status-badge stock-status-badge--in-stock">Availability: In Stock</span>';
      if (vData.inventory_policy === 'continue')
        return '<span class="stock-status-badge stock-status-badge--backorder">Availability: <b>Backorder</b></span>';
      return '<span class="stock-status-badge stock-status-badge--out-of-stock">Out Of Stock</span>';
    }
    return vData.available
      ? '<span class="stock-status-badge stock-status-badge--in-stock">Availability: In Stock</span>'
      : '<span class="stock-status-badge stock-status-badge--out-of-stock">Out Of Stock</span>';
  }

  function isOutOfStock(vData) {
    if (!vData) return false;
    if (vData.inventory_management === 'shopify') {
      return vData.qty <= 0 && vData.inventory_policy !== 'continue';
    }
    return vData.available === false;
  }

  function buildBadgeHTML(statusRaw, dateStr, qty, dateFormat, useDate, fallbackInventory, vData) {
    // Custom status will now be shown for out-of-stock variants as well
    // The suppression logic has been removed to allow custom status display

    if (!statusRaw) {
      return (fallbackInventory && vData) ? buildInventoryBadge(vData) : '';
    }

    const status = statusRaw.toLowerCase().trim();

    if (status === 'on sale')
      return '<span class="stock-status-badge stock-status-badge--on-sale"><b>On Sale</b></span>';

    if (status === 'availability: backorder')
      return '<span class="stock-status-badge stock-status-badge--backorder">Availability: <b>Backorder</b></span>';

    if (status.includes('product will be available on')) {
      // If the raw status contains the {expected_date} placeholder, substitute it
      // with the formatted date from the stock_expected_date metafield.
      if (statusRaw.includes('{expected_date}')) {
        if (dateStr) {
          const exp = new Date(dateStr + 'T00:00:00Z').getTime();
          // Expected date is in the past — fall back to inventory-based status
          if (exp <= Date.now()) {
            return (fallbackInventory && vData) ? buildInventoryBadge(vData) : '';
          }
          const dateLabel = formatDate(dateStr, dateFormat);
          const displayText = statusRaw.replace('{expected_date}', dateLabel);
          return `<span class="stock-status-badge stock-status-badge--coming-soon">${escapeHTML(displayText)}</span>`;
        }
        // No date set — fall back to inventory-based status
        return (fallbackInventory && vData) ? buildInventoryBadge(vData) : '';
      }
      // Legacy: status text already contains the full date string
      return `<span class="stock-status-badge stock-status-badge--coming-soon">${escapeHTML(statusRaw)}</span>`;
    }

    if (status.includes('will be available {day-after-tomorrow}'))
      return `<span class="stock-status-badge stock-status-badge--coming-soon">Will be available ${offsetDateFormatted(2, dateFormat)}</span>`;

    if (status.includes('left!')) {
      const q = parseInt(qty, 10) || 0;
      return q > 0 ? `<span class="stock-status-badge stock-status-badge--low-stock">${q} LEFT!</span>` : '';
    }

    if (status === 'availability: special order')
      return '<span class="stock-status-badge stock-status-badge--special-order">Availability: <b>Special Order</b></span>';

    if (status.includes('drop ship'))
      return '<span class="stock-status-badge stock-status-badge--drop-ship">Availability: <b>Drop Ship</b> **LEAD TIME</span>';

    if (status === 'condition: used')
      return '<span class="stock-status-badge stock-status-badge--condition-used">Condition: Used</span>';

    if (status === 'availability: in stock')
      return '<span class="stock-status-badge stock-status-badge--in-stock">Availability: In Stock</span>';

    if (status === 'downloadable product')
      return '<span class="stock-status-badge stock-status-badge--downloadable">Downloadable Product</span>';

    if (status === 'discontinued')
      return '<span class="stock-status-badge stock-status-badge--discontinued">Discontinued</span>';

    if (status === 'out of stock')
      return '<span class="stock-status-badge stock-status-badge--out-of-stock">Out Of Stock</span>';

    if (status === 'open box')
      return '<span class="stock-status-badge stock-status-badge--open-box">Open Box</span>';

    return `<span class="stock-status-badge stock-status-badge--custom">${escapeHTML(statusRaw)}</span>`;
  }

  function getInitialVariantId(productInfoEl) {
    const el = productInfoEl && productInfoEl.querySelector('[data-selected-variant]');
    if (!el) return null;
    try { const v = JSON.parse(el.textContent); return v ? v.id : null; } catch (e) { return null; }
  }

  function initStockStatus() {
    // Read product ID from the stock-status wrapper itself (most reliable source)
    const wrapper = document.querySelector('.stock-status-wrapper[data-context="pdp"]');
    if (!wrapper) return;

    const productId = wrapper.dataset.productId;
    if (!productId) return;

    const dataEl = document.getElementById('stock-status-data-' + productId);
    if (!dataEl) return;

    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    function updateBadge(variantId) {
      if (!variantId) return;
      const vData = data.variants && data.variants[String(variantId)];
      let statusRaw, dateStr, qty;

      if (vData && vData.status) {
        statusRaw = vData.status;
        dateStr   = vData.date || data.productDate || null;
        qty       = vData.qty  != null ? vData.qty : 0;
      } else {
        statusRaw = data.productStatus || null;
        dateStr   = (vData && vData.date) ? vData.date : (data.productDate || null);
        qty       = (vData && vData.qty  != null) ? vData.qty : 0;
      }

      wrapper.innerHTML = buildBadgeHTML(
        statusRaw, dateStr, qty,
        data.dateFormat        || '%B %d, %Y',
        data.useDate           !== false,
        data.fallbackInventory !== false,
        vData
      );
    }

    // Subscribe to Dawn's pub/sub variantChange event.
    // PUB_SUB_EVENTS.variantChange = 'variant-change', published by product-info.js
    // after each async variant fetch completes.
    // Use the string key directly to avoid any const-scope timing issues.
    const VARIANT_CHANGE_EVENT = 'variant-change';
    if (typeof subscribe === 'function') {
      subscribe(VARIANT_CHANGE_EVENT, function (payload) {
        const variant = payload && payload.data && payload.data.variant;
        if (variant) updateBadge(variant.id);
      });
    }

    // Apply initial state
    const productInfoEl = document.querySelector('product-info');
    const initialVariantId = getInitialVariantId(productInfoEl);
    if (initialVariantId) updateBadge(initialVariantId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStockStatus);
  } else {
    initStockStatus();
  }

})();
